import { IconButton, Slider, Text, Flex, Tooltip } from "@radix-ui/themes";
import { SystemTimeInfoPopover } from "./system-time-info-popover";
import { useEffect, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "@/app/appStore";
import {
  selectHighlightedTimelineEventId,
  selectIsEditable,
  selectOrderTimelineEvents,
  selectSelectedJourneyId,
  setCurrentCamera,
  setGondolaByName,
  setGondolaDropdownValue,
  setGondolaDropdownValueByName,
  setHighlightedTimelineEventId,
  setPlanogramViewVisibility,
  setSelectedEvent,
  setSelectedEventById,
  setSelectedGondola,
  setSelectedJourneyId,
  TimelineEvent,
  selectSelectedOrder,
  selectIsPlaybackLoaded,
  selectIsSkuSectionPlanogramVisible,
  selectCurrentCamera,
} from "@/entities/annotation";
import { Player, useMediaState, usePlayer } from "@tezzro/player";
import { toast } from "react-hot-toast";
import { useTrackedHotkeys } from "@/shared/hooks/useTrackedHotkeys";
import { MagnifierMinusIcon, MagnifierPlusIcon } from "@/assets/icons";
import { ANNOTATION_TYPES } from "@/entities/annotation/model/consts";
import { useCameras } from "../hooks/useCameras";
import { allOption } from "./gondola-select";
import { useTimelineData } from "../hooks/useTimelineData";
import { useGondolaChangeGroupsData } from "@/features/gondola-change-groups/hooks";
import {
  selectClosestGondolaDistItems,
  selectSelectedClosestGondolaDistItem,
  setSelectedClosestGondolaDistItem,
} from "@/entities/gondola-change-groups/model/slice";
import { ClosestGondolaDistItem } from "@/entities/gondola-change-groups";
import {
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
  ThickArrowLeftIcon,
  ThickArrowRightIcon,
} from "@radix-ui/react-icons";
import { TimelineRangeInputs } from "./timeline-range-inputs";
import { SeekAmount } from "./seek-amount";

export const NAVIGATION_MODES = {
  ANNOTATION: "annotation",
  GONDOLA_CHANGES: "gondola-changes",
} as const;

export type NavigationMode =
  (typeof NAVIGATION_MODES)[keyof typeof NAVIGATION_MODES];

export interface TimelineControlsProps {
  onUpdateZoom?: (value: number) => void;
  onRangeChange: (params: {
    start: Date;
    end: Date;
    beforeMinutes: number;
    afterMinutes: number;
  }) => void;
  navigationMode?: NavigationMode;
}

export interface TimelineRangeChangeParams {
  start: Date;
  end: Date;
  beforeMinutes: number;
  afterMinutes: number;
}

export function TimelineControls({
  onUpdateZoom,
  onRangeChange,
  navigationMode = NAVIGATION_MODES.ANNOTATION,
}: TimelineControlsProps) {
  return (
    <div className="flex justify-end items-center gap-4 border-b border-[#CED4DA] px-4 h-14">
      <SeekAmount />
      <TimelineRangeInputs onRangeChange={onRangeChange} />
      <div className="grow" />
      <LiveIndicator />
      <Player.AnonymousModeToggle />
      <PlayPauseButton navigationMode={navigationMode} />
      <Player.CurrentTime />
      <ZoomSlider onUpdateZoom={onUpdateZoom} />
      <SystemTimeInfoPopover />
    </div>
  );
}

interface PlayPauseButtonProps {
  navigationMode?: NavigationMode;
}

function PlayPauseButton({
  navigationMode = NAVIGATION_MODES.ANNOTATION,
}: PlayPauseButtonProps) {
  const player = usePlayer();
  const { dataset, groups, showSavedOnly, highlightTimelineItem } =
    useTimelineData();
  const dispatch = useAppDispatch();
  const selectedOrder = useAppSelector(selectSelectedOrder);
  const isPlaybackLoaded = useAppSelector(selectIsPlaybackLoaded);
  const isSkuSectionPlanogramVisible = useAppSelector(
    selectIsSkuSectionPlanogramVisible
  );
  const orderTimelineEvents = useAppSelector(selectOrderTimelineEvents);
  const sSelectedJourneyId = useAppSelector(selectSelectedJourneyId);
  const isEditable = useAppSelector(selectIsEditable);
  const currentTime = useMediaState("currentTime");
  const highlightedTimelineEventId = useAppSelector(
    selectHighlightedTimelineEventId
  );
  const closestGondolaDistItems = useAppSelector(selectClosestGondolaDistItems);
  const selectedClosestGondolaDistItem = useAppSelector(
    selectSelectedClosestGondolaDistItem
  );
  const currentCamera = useAppSelector(selectCurrentCamera);
  const { selectedGondolaChange } = useGondolaChangeGroupsData();

  const cameraData = useCameras();

  /**
   * Highlight the first gondola change event when the selected gondola change changes
   */
  useEffect(() => {
    const gondolaChangeEvents = getGondolaChangeTimelineEvents();
    const firstEvent = gondolaChangeEvents?.[0];

    if (firstEvent) {
      dispatch(setHighlightedTimelineEventId(firstEvent.id));
      highlightTimelineItem(selectedOrder?.id || null, firstEvent.id);
    }
  }, [selectedGondolaChange?.gondola_change_camera_composite_id]);

  useEffect(() => {
    if (highlightedTimelineEventId !== selectedOrder?.id) {
      highlightTimelineItem(
        selectedOrder?.id || null,
        highlightedTimelineEventId
      );
    }
  }, [highlightedTimelineEventId, selectedOrder]);

  const getGondolaChangeTimelineEvents = useCallback(() => {
    if (!selectedGondolaChange) {
      return [];
    }

    const allItems = dataset.get();
    const selectedGondolaChangeId =
      selectedGondolaChange.gondola_change_camera_composite_id;

    const gondolaChangeEvents = allItems.filter((item) => {
      if (!item.id || typeof item.id !== "string") return false;
      const itemId = item.id as string;

      const isGondolaChangeEvent =
        (itemId === `${selectedGondolaChangeId}-start` ||
          itemId === `${selectedGondolaChangeId}-end`) &&
        item.parentGroup?.toString().startsWith("gondola-changes-");

      return isGondolaChangeEvent;
    });

    return gondolaChangeEvents.map((item) => {
      const itemId = item.id as string;
      const cameraId = extractCameraIdFromGondolaEvent(item);
      const gondolaName = extractGondolaNameFromGondolaEvent(item);

      return {
        id: itemId,
        timestamp:
          item.start instanceof Date
            ? item.start.toISOString()
            : new Date(item.start as any).toISOString(),
        cameraId: cameraId || selectedGondolaChange.camera_local_id,
        type: "GONDOLA_CHANGE" as any,
        journeyId: null,
        gondolaName: gondolaName || selectedGondolaChange.gondola_name,
      };
    });
  }, [dataset, groups, selectedGondolaChange]);

  const extractCameraIdFromGondolaEvent = (item: any): string | null => {
    const allGroups = groups.get();
    const cameraGroup = allGroups.find((g: any) => g.id === item.group);
    if (
      cameraGroup?.content &&
      typeof cameraGroup.content === "string" &&
      cameraGroup.content.startsWith("Camera ")
    ) {
      return cameraGroup.content.replace("Camera ", "");
    }
    return null;
  };

  const extractGondolaNameFromGondolaEvent = (item: any): string | null => {
    const allGroups = groups.get();
    const mainGroup = allGroups.find((g: any) => g.id === item.parentGroup);
    if (mainGroup?.content && typeof mainGroup.content === "string") {
      const match = mainGroup.content.match(/Gondola Changes \((.+)\)/);
      return match ? match[1] : null;
    }
    return null;
  };

  const jumpIntoEvent = (event: TimelineEvent) => {
    const isOrderCreationEvent = event.id === selectedOrder?.id;

    player.seek(event?.timestamp, event.cameraId || undefined);

    if (isOrderCreationEvent) {
      dispatch(setSelectedEvent(null));

      if (!isEditable) {
        dispatch(setSelectedJourneyId(null));
      }
    } else {
      if (navigationMode === NAVIGATION_MODES.GONDOLA_CHANGES) {
        dispatch(setSelectedEvent(null));
      } else {
        dispatch(setSelectedEventById(event.id?.split("_")[0]));
      }

      if (event?.cameraId !== null && event?.cameraId !== undefined) {
        dispatch(setCurrentCamera(event.cameraId));
        cameraData.setCameraModeFromCameraId(event.cameraId);
      }

      if (
        event.journeyId !== sSelectedJourneyId &&
        !isEditable &&
        navigationMode === NAVIGATION_MODES.ANNOTATION
      ) {
        dispatch(setSelectedJourneyId(event.journeyId));
      }
    }

    const gondolaName =
      event?.gondolaName ||
      cameraData?.findCameraGondolaName?.(event.cameraId as string);

    if (gondolaName) {
      dispatch(setGondolaDropdownValueByName(gondolaName));
      dispatch(setGondolaByName(gondolaName));
    } else {
      dispatch(setSelectedGondola(null));
      dispatch(setGondolaDropdownValue(allOption));
    }

    if (event?.type === ANNOTATION_TYPES.TRANSACTION) {
      dispatch(setPlanogramViewVisibility(false));
    }

    dispatch(setHighlightedTimelineEventId(event.id));
    highlightTimelineItem(selectedOrder?.id || null, event.id);
  };

  const sortTimelineEvents = (events: TimelineEvent[]): TimelineEvent[] => {
    return events.sort((a, b) => {
      const timeA = new Date(a.timestamp).getTime();
      const timeB = new Date(b.timestamp).getTime();

      if (timeA !== timeB) {
        return timeA - timeB;
      }

      return a.id.localeCompare(b.id);
    });
  };

  const findNextFromSelected = (
    sortedEvents: TimelineEvent[],
    currentIndex: number,
    direction: "forward" | "backward",
    navigationMode: NavigationMode
  ): TimelineEvent | null => {
    if (direction === "forward") {
      const nextIndex = currentIndex + 1;

      if (navigationMode === NAVIGATION_MODES.GONDOLA_CHANGES) {
        return nextIndex < sortedEvents.length ? sortedEvents[nextIndex] : null;
      }

      return sortedEvents[nextIndex % sortedEvents.length];
    } else {
      const prevIndex = currentIndex - 1;

      if (navigationMode === NAVIGATION_MODES.GONDOLA_CHANGES) {
        return prevIndex >= 0 ? sortedEvents[prevIndex] : null;
      }

      return sortedEvents[
        (prevIndex + sortedEvents.length) % sortedEvents.length
      ];
    }
  };

  const findNextFromTime = (
    sortedEvents: TimelineEvent[],
    currentTime: number,
    direction: "forward" | "backward",
    navigationMode: NavigationMode
  ): TimelineEvent | null => {
    if (direction === "forward") {
      const nextEvent = sortedEvents.find((event) => {
        const eventTime = new Date(event.timestamp).getTime();
        return eventTime >= currentTime;
      });

      if (navigationMode === NAVIGATION_MODES.GONDOLA_CHANGES) {
        return nextEvent || null;
      }
      return nextEvent || sortedEvents[0];
    } else {
      const reversedEvents = [...sortedEvents].reverse();
      const prevEvent = reversedEvents.find((event) => {
        const eventTime = new Date(event.timestamp).getTime();
        return eventTime <= currentTime;
      });

      if (navigationMode === NAVIGATION_MODES.GONDOLA_CHANGES) {
        return prevEvent || null;
      }
      return prevEvent || sortedEvents[sortedEvents.length - 1];
    }
  };

  const findNextEvent = (
    eventTimestamps: TimelineEvent[],
    direction: "forward" | "backward",
    navigationMode: NavigationMode
  ): TimelineEvent | null => {
    const sortedEvents = sortTimelineEvents(eventTimestamps);

    const eventId = highlightedTimelineEventId;

    const currentIndex = sortedEvents.findIndex(
      (event) => event.id === eventId
    );

    if (currentIndex !== -1 && highlightedTimelineEventId) {
      return findNextFromSelected(
        sortedEvents,
        currentIndex,
        direction,
        navigationMode
      );
    } else if (currentTime !== undefined) {
      return findNextFromTime(
        sortedEvents,
        currentTime,
        direction,
        navigationMode
      );
    } else {
      throw new Error("Either selectedEvent or currentTime must be provided");
    }
  };

  const navigateThroughEvents = (direction: "forward" | "backward") => {
    if (!selectedOrder) return;

    if (isSkuSectionPlanogramVisible) {
      toast.error("Complete the current annotation before proceeding");
      return;
    }

    if (!orderTimelineEvents?.length) return;

    const eventTimestamps = orderTimelineEvents;

    if (showSavedOnly) {
      const allGroups = groups.get();
      const visibleChildGroups = allGroups.filter(
        (group) => !group.nestedGroups && group.visible && group.isSaved
      );
      const visibleGroupIds = visibleChildGroups.map((group) => group.id);
      const allDatasetItems = dataset.get();
      const visibleDatasetItems = allDatasetItems.filter(
        (item) =>
          item.group !== undefined && visibleGroupIds.includes(item.group)
      );
      const visibleItemIds = visibleDatasetItems.map((item) => item.id);
      const filteredEventTimestamps = eventTimestamps.filter((event) =>
        visibleItemIds.includes(event.id)
      );

      const nextEvent = findNextEvent(
        filteredEventTimestamps,
        direction,
        NAVIGATION_MODES.ANNOTATION
      );

      if (nextEvent) {
        jumpIntoEvent(nextEvent);
      }
    } else {
      const nextEvent = findNextEvent(
        eventTimestamps,
        direction,
        NAVIGATION_MODES.ANNOTATION
      );

      if (nextEvent) {
        jumpIntoEvent(nextEvent);
      }
    }
  };

  const navigateGondolaChanges = (direction: "forward" | "backward") => {
    if (!selectedOrder) return;

    const eventTimestamps = getGondolaChangeTimelineEvents();

    if (!eventTimestamps.length) {
      toast.error("No gondola change events available for navigation");
      return;
    }

    const nextEvent = findNextEvent(
      eventTimestamps,
      direction,
      NAVIGATION_MODES.GONDOLA_CHANGES
    );

    if (nextEvent) {
      jumpIntoEvent(nextEvent);
    }
  };

  const findNextClosestGondolaDistFromSelected = (
    sortedItems: ClosestGondolaDistItem[],
    currentIndex: number,
    direction: "forward" | "backward"
  ): ClosestGondolaDistItem | null => {
    if (direction === "forward") {
      const nextIndex = currentIndex + 1;
      return nextIndex < sortedItems.length ? sortedItems[nextIndex] : null;
    } else {
      const prevIndex = currentIndex - 1;
      return prevIndex >= 0 ? sortedItems[prevIndex] : null;
    }
  };

  const findNextClosestGondolaDistFromTime = (
    sortedItems: ClosestGondolaDistItem[],
    currentTime: number,
    direction: "forward" | "backward"
  ): ClosestGondolaDistItem | null => {
    if (direction === "forward") {
      const nextItem = sortedItems.find((item) => {
        const itemTime = new Date(item.timestamp).getTime();
        return itemTime >= currentTime;
      });
      return nextItem || null;
    } else {
      const reversedItems = [...sortedItems].reverse();
      const prevItem = reversedItems.find((item) => {
        const itemTime = new Date(item.timestamp).getTime();
        return itemTime <= currentTime;
      });
      return prevItem || null;
    }
  };

  const navigateClosestGondolaDistTimes = useCallback(
    (direction: "forward" | "backward") => {
      if (!selectedOrder || !closestGondolaDistItems.length) {
        toast.error(
          "No closest gondola distance times available for navigation"
        );
        return;
      }

      const sortedItems = [...closestGondolaDistItems].sort(
        (a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );

      let nextItem: ClosestGondolaDistItem | null = null;

      const useTimeBasedNavigation =
        currentTime !== undefined &&
        (!selectedClosestGondolaDistItem ||
          !highlightedTimelineEventId ||
          (direction === "forward" &&
            currentTime < new Date(sortedItems[0].timestamp).getTime()) ||
          (direction === "backward" &&
            currentTime >
              new Date(
                sortedItems[sortedItems.length - 1].timestamp
              ).getTime()));

      if (useTimeBasedNavigation && currentTime !== undefined) {
        nextItem = findNextClosestGondolaDistFromTime(
          sortedItems,
          currentTime,
          direction
        );
      } else if (selectedClosestGondolaDistItem) {
        const currentIndex = sortedItems.findIndex(
          (item) => item.id === selectedClosestGondolaDistItem.id
        );

        if (currentIndex !== -1) {
          nextItem = findNextClosestGondolaDistFromSelected(
            sortedItems,
            currentIndex,
            direction
          );
        }
      }

      if (nextItem) {
        dispatch(setSelectedClosestGondolaDistItem(nextItem));

        const availableCameras = Object.keys(
          nextItem.projected_wrist_points_2d_per_cam
        );
        const targetCamera = availableCameras.includes(currentCamera)
          ? currentCamera
          : availableCameras[0];

        if (targetCamera) {
          dispatch(setCurrentCamera(targetCamera));
        }

        const timelineEventId = `selected-proximity-${nextItem.track_id}-${nextItem.timestamp}`;
        dispatch(setHighlightedTimelineEventId(timelineEventId));
        highlightTimelineItem(selectedOrder?.id || null, timelineEventId);

        player.seek(nextItem.timestamp, targetCamera || currentCamera);
      }
    },
    [
      selectedOrder,
      closestGondolaDistItems,
      selectedClosestGondolaDistItem,
      highlightedTimelineEventId,
      currentTime,
      dispatch,
      currentCamera,
      highlightTimelineItem,
      player,
    ]
  );

  useTrackedHotkeys(
    "mod+right",
    (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      navigateThroughEvents("forward");
    },
    {
      enabled:
        isPlaybackLoaded && navigationMode === NAVIGATION_MODES.ANNOTATION,
    }
  );

  useTrackedHotkeys(
    "mod+left",
    (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      navigateThroughEvents("backward");
    },
    {
      enabled:
        isPlaybackLoaded && navigationMode === NAVIGATION_MODES.ANNOTATION,
    }
  );

  useTrackedHotkeys(
    "shift+right",
    (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      navigateGondolaChanges("forward");
    },
    {
      enabled:
        isPlaybackLoaded && navigationMode === NAVIGATION_MODES.GONDOLA_CHANGES,
    }
  );

  useTrackedHotkeys(
    "shift+left",
    (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      navigateGondolaChanges("backward");
    },
    {
      enabled:
        isPlaybackLoaded && navigationMode === NAVIGATION_MODES.GONDOLA_CHANGES,
    }
  );

  // Closest gondola dist times navigation shortcuts
  useTrackedHotkeys(
    "ctrl+shift+right",
    (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      navigateClosestGondolaDistTimes("forward");
    },
    {
      enabled:
        isPlaybackLoaded && navigationMode === NAVIGATION_MODES.GONDOLA_CHANGES,
    }
  );

  useTrackedHotkeys(
    "ctrl+shift+left",
    (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      navigateClosestGondolaDistTimes("backward");
    },
    {
      enabled:
        isPlaybackLoaded && navigationMode === NAVIGATION_MODES.GONDOLA_CHANGES,
    }
  );

  const onPreviousGondolaChange = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    navigateGondolaChanges("backward");
  };

  const onNextGondolaChange = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    navigateGondolaChanges("forward");
  };

  const onPreviousClosestGondolaDist = (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.preventDefault();
    navigateClosestGondolaDistTimes("backward");
  };

  const onNextClosestGondolaDist = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    navigateClosestGondolaDistTimes("forward");
  };

  return (
    <>
      {navigationMode === NAVIGATION_MODES.GONDOLA_CHANGES && (
        <>
          <Tooltip content="Jump to gondola change start time">
            <IconButton
              variant="ghost"
              className="m-0"
              size="3"
              color="gray"
              highContrast
              aria-label="Previous gondola change"
              onClick={onPreviousGondolaChange}
            >
              <DoubleArrowLeftIcon />
            </IconButton>
          </Tooltip>
          <Tooltip content="Jump to gondola change end time">
            <IconButton
              variant="ghost"
              className="m-0"
              size="3"
              color="gray"
              highContrast
              aria-label="Next gondola change"
              onClick={onNextGondolaChange}
            >
              <DoubleArrowRightIcon />
            </IconButton>
          </Tooltip>
          <Tooltip content="Jump to previous closest gondola dist time">
            <IconButton
              variant="ghost"
              className="m-0"
              size="3"
              color="gray"
              highContrast
              aria-label="Previous gondola change"
              onClick={onPreviousClosestGondolaDist}
            >
              <ThickArrowLeftIcon />
            </IconButton>
          </Tooltip>
          <Tooltip content="Jump to next closest gondola dist time">
            <IconButton
              variant="ghost"
              className="m-0"
              size="3"
              color="gray"
              highContrast
              aria-label="Previous gondola change"
              onClick={onNextClosestGondolaDist}
            >
              <ThickArrowRightIcon />
            </IconButton>
          </Tooltip>
        </>
      )}

      <Player.PlayPauseControl hotkeysEnabled />
    </>
  );
}

export function ZoomSlider({
  onUpdateZoom,
}: {
  onUpdateZoom?: (value: number) => void;
}) {
  const ZOOM_STEP = 0.05;
  const zoomLevel = useAppSelector((state) => state.annotation.zoomLevel);

  return (
    <>
      <IconButton
        variant="ghost"
        className="m-0"
        size="3"
        color="gray"
        highContrast
        onClick={() => {
          if (!zoomLevel) return;

          const newZoom = Math.max(zoomLevel - ZOOM_STEP, 0);

          if (newZoom < zoomLevel) {
            onUpdateZoom?.(newZoom);
          }
        }}
      >
        <MagnifierMinusIcon className="text-brand-gray-100" />
      </IconButton>
      <Slider
        className="relative flex items-center select-none touch-none max-w-[200px] h-5"
        defaultValue={[25]}
        max={1}
        min={0}
        step={ZOOM_STEP}
        color="gray"
        highContrast
        size="1"
        value={[zoomLevel ?? 0]}
        onValueChange={([value]) => {
          onUpdateZoom?.(value);
        }}
        onKeyDown={(e) => {
          e.preventDefault();
        }}
        onPointerUp={(e) => {
          (e.target as HTMLElement).blur();
        }}
      />
      <IconButton
        variant="ghost"
        className="m-0"
        size="3"
        color="gray"
        highContrast
        onClick={() => {
          if (!zoomLevel) return;

          const newZoom = Math.min(zoomLevel + ZOOM_STEP, 1);

          if (newZoom > zoomLevel) {
            onUpdateZoom?.(newZoom);
          }
        }}
      >
        <MagnifierPlusIcon className="text-brand-gray-100" />
      </IconButton>
    </>
  );
}

export function LiveIndicator() {
  const isLive = useMediaState("isLive");
  const liveDelay = useMediaState("liveDelay");

  if (!isLive) {
    return null;
  }

  return (
    <Flex align="center" justify="center">
      <Text size="2">Live delay:</Text>
      <span className="text-black-500 ml-1 mr-3 text-sm">
        {liveDelay.toFixed(1)}s
      </span>

      <Player.LiveControl />
    </Flex>
  );
}
